import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import {
  People,
  EventNote,
  CardGiftcard,
  Business,
  Settings,
  ChevronRight,
  Logout,
  Language
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Manage - Mobile-First Management Hub
 */
function LibrarianManage({ showSnackbar }) {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const menuSections = [
    {
      title: 'Management',
      items: [
        {
          id: 'plans',
          icon: <CardGiftcard color="primary" />,
          label: 'Plans',
          description: 'Manage membership plans',
          action: () => navigate('/librarian/plans')
        }
      ]
    },
    {
      title: 'Library',
      items: [
        {
          id: 'library-details',
          icon: <Business color="primary" />,
          label: 'Library QR',
          description: 'View library info & QR',
          action: () => navigate('/librarian/library-details')
        },
        {
          id: 'settings',
          icon: <Settings color="primary" />,
          label: 'Edit Profile',
          description: 'Edit library information',
          action: () => navigate('/librarian/settings')
        }
      ]
    },
    {
      title: 'Account',
      items: [
        {
          id: 'logout',
          icon: <Logout color="error" />,
          label: 'Logout',
          description: 'Sign out of your account',
          action: () => {
            navigate('/login');
            setTimeout(() => logout(), 0);
          },
          danger: true
        }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Access management tools and settings
            </Typography>
          </MotionBox>

          {/* User Card */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 3,
              background: GRADIENTS.brand,
              color: 'white'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}
                >
                  {user?.name?.charAt(0)}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight="700" gutterBottom>
                    {user?.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {user?.email}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderRadius: RADIUS.sm,
                      px: 1,
                      py: 0.5,
                      mt: 0.5,
                      display: 'inline-block'
                    }}
                  >
                    {user?.role?.toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Menu Sections */}
          {menuSections.map((section, sectionIndex) => (
            <MotionBox key={section.title} variants={itemVariants} mb={2}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="700"
                textTransform="uppercase"
                sx={{ px: 2, mb: 1, display: 'block' }}
              >
                {section.title}
              </Typography>
              <Card sx={{ borderRadius: RADIUS.xl }}>
                <CardContent sx={{ p: 0 }}>
                  <List disablePadding>
                    {section.items.map((item, itemIndex) => (
                      <React.Fragment key={item.id}>
                        {itemIndex > 0 && <Divider />}
                        <ListItemButton
                          onClick={item.action}
                          sx={{
                            py: 2,
                            px: 2,
                            '&:hover': {
                              bgcolor: item.danger ? 'rgba(244, 67, 54, 0.08)' : 'action.hover'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 48 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: item.danger ? 'rgba(244, 67, 54, 0.1)' : 'action.hover'
                              }}
                            >
                              {item.icon}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                fontWeight="600"
                                color={item.danger ? 'error.main' : 'text.primary'}
                              >
                                {item.label}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {item.description}
                              </Typography>
                            }
                          />
                          <ChevronRight
                            color={item.danger ? 'error' : 'action'}
                            fontSize="small"
                          />
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </MotionBox>
          ))}

          {/* App Info */}
          <Box textAlign="center" mt={4} mb={2}>
            <Typography variant="caption" color="text.secondary">
              BookMyLibrary v1.0.0
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              &copy; {new Date().getFullYear()} All rights reserved
            </Typography>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibrarianManage;
